<template>
  <section
    class="category-section"
    :class="{ 'is-active': active, 'drag-over': isDragOver, 'is-list': layout === 'list' }"
    @click="activate"
    @focusin="activate"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <header v-if="layout === 'grid'" class="category-header">
      <span class="category-icon" :style="{ color: theme.color, background: theme.tint }" aria-hidden="true">
        <i :class="theme.icon"></i>
      </span>
      <h3 class="category-title">{{ title }}</h3>
      <span class="category-count" :aria-label="`${items.length} ${unit}`">{{ items.length }}</span>
      <span class="category-spacer"></span>
      <button
        v-if="category && items.length > 0"
        type="button"
        class="category-link"
        @click.stop="$emit('select-all', category)"
      >
        เลือกทั้งหมดในหมวดนี้
      </button>
      <template v-if="allowUpload">
        <button
          v-if="canCapture"
          type="button"
          class="category-camera"
          :aria-label="`ถ่ายรูปลง ${title}`"
          @click.stop="$refs.cameraInput.click()"
        >
          <i class="fas fa-camera" aria-hidden="true"></i>
        </button>
        <input
          ref="cameraInput"
          type="file"
          class="d-none"
          accept="image/jpeg,image/png"
          capture="environment"
          @change="onCaptured"
        />
      </template>
    </header>

    <!-- ลากวาง/วาง/ถ่ายรูป: ถามวันที่รักษาและคำอธิบายเหมือนหน้าอัปโหลด ก่อนส่ง -->
    <div v-if="pending" class="drop-confirm" @click.stop>
      <div class="drop-confirm-row">
        <div class="drop-confirm-summary">
          <strong v-if="pending.accepted.length">
            เพิ่ม {{ pending.accepted.length }} ไฟล์ลง {{ title }}
          </strong>
          <strong v-else>ไม่มีไฟล์ที่เพิ่มได้</strong>
          <span class="muted">{{ pending.source }} · ตรวจประเภทและขนาดแล้ว</span>
        </div>
        <template v-if="pending.accepted.length">
          <div class="drop-confirm-field date">
            <label :for="`${uid}-date`">วันที่รักษา</label>
            <b-form-select
              :id="`${uid}-date`"
              v-model="treatmentId"
              size="sm"
              :options="treatmentOptions"
            />
          </div>
          <div class="drop-confirm-field remark">
            <label :for="`${uid}-remark`">คำอธิบาย</label>
            <b-form-input
              :id="`${uid}-remark`"
              v-model="remark"
              size="sm"
              placeholder="เช่น ก่อนจัดฟัน"
              @keyup.enter="confirmUpload"
            />
          </div>
        </template>
        <div class="drop-confirm-actions">
          <b-button size="sm" variant="outline-secondary" class="pill" @click="cancelPending">
            {{ pending.accepted.length ? "ยกเลิก" : "ปิด" }}
          </b-button>
          <b-button
            v-if="pending.accepted.length"
            size="sm"
            variant="primary"
            class="pill"
            :disabled="isSending"
            @click="confirmUpload"
          >
            อัปโหลด {{ pending.accepted.length }} ไฟล์
          </b-button>
        </div>
      </div>
      <div v-if="pending.rejected.length" class="drop-confirm-rejected">
        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
        ข้าม {{ pending.rejected.length }} ไฟล์:
        {{ pending.rejected.map(r => `${r.name} — ${r.reason}`).join(", ") }}
      </div>
    </div>

    <div v-if="items.length === 0 && allowUpload && !pending" class="category-empty">
      <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
      <span>
        {{ emptyLabel }} —
        {{ isTouch ? "" : "ลากไฟล์มาวาง หรือ" }}
      </span>
      <button type="button" class="category-link strong" @click.stop="$emit('upload', category)">
        เลือกไฟล์
      </button>
    </div>

    <div v-if="layout === 'list' && items.length > 0" class="list-table">
      <div class="list-head" aria-hidden="true">
        <span></span><span>เอกสาร</span><span>วันที่รักษา</span><span>อัปโหลดเมื่อ</span><span>สาขา</span><span></span>
      </div>
      <DocumentRow
        v-for="item in visibleItems"
        :key="item.id"
        :item="item"
        :is-selected="selectedIds.has(item.id)"
        :file-name="fileNameOf ? fileNameOf(item) : ''"
        @select="$emit('select', $event)"
        @preview="$emit('preview', $event)"
        @download="$emit('download', $event)"
        @delete="$emit('delete', $event)"
        @sign="$emit('sign', $event)"
      />
      <button v-if="hiddenCount > 0" type="button" class="list-more" @click.stop="expanded = true">
        ดูอีก {{ hiddenCount }} {{ unit }}
      </button>
    </div>

    <div v-else-if="items.length > 0" class="category-grid">
      <FileItem
        v-for="item in visibleItems"
        :key="item.id"
        :item="item"
        :is-selected="selectedIds.has(item.id)"
        :selection-mode="selectedFiles.length > 0"
        @select="$emit('select', $event)"
        @preview="$emit('preview', $event)"
        @download="$emit('download', $event)"
        @delete="$emit('delete', $event)"
        @sign="$emit('sign', $event)"
        @radiiView="$emit('radiiView', $event)"
      />
      <button
        v-if="hiddenCount > 0"
        type="button"
        class="category-more"
        @click.stop="expanded = true"
      >
        ดูอีก {{ hiddenCount }} {{ unit }}
      </button>
    </div>
    <button
      v-if="expanded && items.length > collapsedLimit"
      type="button"
      class="category-link"
      @click.stop="expanded = false"
    >
      ย่อ
    </button>

    <div v-if="isDragOver" class="drag-overlay">
      <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
      วางไฟล์ลง {{ title }}
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FileItem from "./FileItem.vue";
import DocumentRow from "../document/DocumentRow.vue";
import { CAMERA_CATEGORIES, checkFiles, contentTypeOf } from "@/utils/uploadRules";
import { categoryTheme } from "@/utils/categoryTheme";

let uidCounter = 0;

/**
 * One category of a patient's files: its grid, and the drop target for new
 * ones. Files dropped, pasted (Ctrl+V while the section is active) or taken
 * with the camera are checked against the category's rules, then wait in a
 * confirm bar for the same treatment date and description the upload dialog
 * asks for. The upload itself shows in the global upload tray.
 */
export default {
  name: "CategoryCard",
  components: {
    FileItem,
    DocumentRow,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    // null for groups that are not a category (by treatment date)
    category: {
      type: String,
      default: null,
    },
    items: {
      type: Array,
      required: true,
    },
    selectedFiles: {
      type: Array,
      required: true,
    },
    patientId: {
      type: [String, Number],
      default: null,
    },
    allowUpload: {
      type: Boolean,
      default: true,
    },
    // this section receives Ctrl+V
    active: {
      type: Boolean,
      default: false,
    },
    unit: {
      type: String,
      default: "รูป",
    },
    collapsedLimit: {
      type: Number,
      default: 16,
    },
    // "grid" of tiles (photos) or "list" of rows (documents)
    layout: {
      type: String,
      default: "grid",
    },
    // the download name of a file, shown under it in the list layout
    fileNameOf: {
      type: Function,
      default: null,
    },
  },
  data() {
    uidCounter += 1;
    return {
      uid: `category-${uidCounter}`,
      isDragOver: false,
      dragDepth: 0,
      // { accepted: File[], rejected: [{ name, reason }], source: string }
      pending: null,
      treatmentId: null,
      remark: "",
      isSending: false,
      expanded: false,
      isTouch:
        typeof window !== "undefined" &&
        !!window.matchMedia &&
        window.matchMedia("(pointer: coarse)").matches,
    };
  },
  computed: {
    ...mapGetters({
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
    }),
    theme() {
      return categoryTheme(this.category);
    },
    selectedIds() {
      return new Set(this.selectedFiles.map(f => f.id));
    },
    visibleItems() {
      return this.expanded ? this.items : this.items.slice(0, this.collapsedLimit);
    },
    hiddenCount() {
      return this.items.length - this.visibleItems.length;
    },
    // "ยังไม่มีเอกสาร", "ยังไม่มี Intra-Oral"
    emptyLabel() {
      return /^[A-Za-z]/.test(this.title) ? `ยังไม่มี ${this.title}` : `ยังไม่มี${this.title}`;
    },
    canCapture() {
      return this.isTouch && CAMERA_CATEGORIES.includes(this.category);
    },
    treatmentOptions() {
      const seen = new Set();
      const treatments = (this.getTreatmentHistoryList || [])
        .filter(t => t && t.id && !seen.has(t.id) && seen.add(t.id))
        .sort((a, b) => new Date(b.creationDt) - new Date(a.creationDt));
      return [
        { value: null, text: "ไม่ระบุวันที่รักษา" },
        ...treatments.map(t => ({
          value: t.id,
          text: this.$utils.formatDateTime(t.creationDt),
        })),
      ];
    },
  },
  methods: {
    ...mapActions({
      createMediaFiles: "moduleMediaFile/createMediaFiles",
    }),
    activate() {
      if (this.allowUpload && this.category) this.$emit("activate", this.category);
    },
    onDragEnter() {
      if (!this.allowUpload) return;
      this.dragDepth += 1;
      this.isDragOver = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.isDragOver = false;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.isDragOver = false;
      if (!this.allowUpload) return;
      this.receiveFiles(Array.from(event.dataTransfer.files || []), "ลากมาวาง");
    },
    onCaptured(event) {
      this.receiveFiles(Array.from(event.target.files || []), "ถ่ายรูป");
      event.target.value = "";
    },
    /**
     * Checks files against the category and asks for the details before
     * uploading. Called for drops, the camera and Ctrl+V (by the gallery).
     */
    receiveFiles(files, source = "ลากมาวาง") {
      if (!files.length || !this.category) return;
      const { accepted, rejected } = checkFiles(files, this.category);
      // วางเพิ่มระหว่างที่แถบยืนยันเปิดอยู่: รวมเป็นชุดเดียว
      const previous = this.pending || { accepted: [], rejected: [] };
      this.pending = {
        accepted: [...previous.accepted, ...accepted],
        rejected: [...previous.rejected, ...rejected.map(r => ({ name: r.name, reason: r.reason }))],
        source,
      };
    },
    cancelPending() {
      this.pending = null;
      this.remark = "";
      this.treatmentId = null;
    },
    async confirmUpload() {
      if (!this.pending || !this.pending.accepted.length || this.isSending) return;
      const files = this.pending.accepted;
      const data = {
        category: this.category,
        takenAt: null,
        patientId: this.patientId ? String(this.patientId) : null,
        treatmentId: this.treatmentId ? String(this.treatmentId) : null,
        labId: null,
        remark: this.remark,
        filesMetadata: files.map(file => ({
          contentType: contentTypeOf(file),
          fileSize: file.size,
        })),
      };
      this.isSending = true;
      try {
        const { completed } = await this.createMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          data,
          files,
          names: files.map(f => f.name),
          // เซิร์ฟเวอร์รับชุดนี้แล้ว ที่เหลือแถบอัปโหลดมุมซ้ายล่างดูแลต่อ
          // วางไฟล์ชุดใหม่ลงหมวดนี้ได้เลยระหว่างรอ
          onPresigned: () => {
            this.cancelPending();
            this.isSending = false;
          },
        });
        this.$emit("upload-success", { category: this.category, files: completed });
      } catch (error) {
        // ส่งไปแล้วแต่ไม่สำเร็จ (หรือผู้ใช้กดยกเลิก): แถบอัปโหลดแจ้งพร้อมปุ่มลองใหม่แล้ว
        if (error.failed) return;
        const msg = error.response?.data?.errorMessage;
        // โควตาเต็ม/แพ็กหมดอายุ store แจ้งเองแล้ว
        if (msg !== "file limit exceeded" && msg !== "file store plan expired.") {
          this.$emit("upload-error", { message: "อัปโหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", files });
        }
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
.category-section {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.125rem 1.25rem 1.25rem;
  margin-bottom: 1rem;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.category-section.is-active {
  border-color: #c9dcf5;
  box-shadow: 0 0 0 3px rgba(11, 99, 197, 0.08);
}
.category-section.is-list,
.category-section.is-list.is-active {
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}
.category-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  min-height: 36px;
}
.category-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.category-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #101828;
}
.category-count {
  padding: 0.0625rem 0.5625rem;
  border-radius: 999px;
  background: #f1f3f6;
  font-size: 0.75rem;
  font-weight: 600;
  color: #3d4557;
}
.muted {
  font-size: 0.8125rem;
  color: #4b5569;
}
.category-spacer {
  flex: 1 1 auto;
}
.category-link {
  min-height: 32px;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 0.8125rem;
  color: var(--primary, #0b63c5);
}
.category-link:hover {
  background: #eef5fe;
}
.category-link.strong {
  padding: 0;
  font-weight: 600;
  font-size: 0.875rem;
}
.category-link.strong:hover {
  background: transparent;
  text-decoration: underline;
}
.category-camera {
  width: 38px;
  height: 38px;
  border: 1px solid #d7dbe3;
  border-radius: 19px;
  background: #fff;
  color: #3d4557;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
  gap: 0.875rem;
}
.category-more {
  aspect-ratio: 1 / 1;
  border: 0;
  border-radius: 14px;
  background: #3d4557;
  color: #fff;
  font-weight: 600;
}
.category-more:hover {
  background: #2c3342;
}

.category-empty {
  min-height: 56px;
  border: 1.5px dashed #cfd4dd;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #5d6679;
  background: #fff;
}

.drop-confirm {
  border: 1px solid #c9dcf5;
  background: #eef5fe;
  border-radius: 14px;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.drop-confirm-row {
  display: flex;
  align-items: flex-end;
  gap: 0.875rem;
  flex-wrap: wrap;
}
.drop-confirm-summary {
  display: flex;
  flex-direction: column;
  min-width: 180px;
  align-self: center;
}
.drop-confirm-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.drop-confirm-field label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: #3d4557;
}
.drop-confirm-field.date {
  width: 220px;
}
.drop-confirm-field.remark {
  flex: 1 1 200px;
}
.drop-confirm-actions {
  display: flex;
  gap: 0.5rem;
}
.drop-confirm .custom-select,
.drop-confirm .form-control {
  min-height: 40px;
  border-radius: 10px;
}
.drop-confirm .pill {
  min-height: 40px;
  padding: 0 1rem;
  border-radius: 999px;
}
.drop-confirm-rejected {
  font-size: 0.75rem;
  color: #8f1d14;
}

.drag-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  border: 2px dashed var(--primary, #0b63c5);
  border-radius: 18px;
  background: rgba(238, 245, 254, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-weight: 600;
  color: var(--primary, #0b63c5);
  pointer-events: none;
}

.list-table {
  border: 1px solid #e8eaef;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.list-head {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 140px 120px 120px auto;
  align-items: center;
  gap: 0.5rem;
  height: 44px;
  padding: 0 1rem;
  background: #fafbfc;
  border-bottom: 1px solid #eef0f3;
  font-size: 0.75rem;
  font-weight: 600;
  color: #5d6679;
}
.list-more {
  width: 100%;
  min-height: 44px;
  border: 0;
  background: #fafbfc;
  color: var(--primary, #0b63c5);
  font-size: 0.875rem;
}

@media (max-width: 767.98px) {
  .list-head {
    display: none;
  }
}

@media (max-width: 575.98px) {
  .category-section {
    padding: 0.75rem;
    border-radius: 16px;
    gap: 0.625rem;
  }
  .category-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }
  .category-link {
    display: none;
  }
  .category-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.375rem;
  }
  .category-more {
    border-radius: 12px;
  }
  .drop-confirm-field.date,
  .drop-confirm-field.remark {
    width: 100%;
    flex-basis: 100%;
  }
  .drop-confirm-actions {
    width: 100%;
  }
  .drop-confirm-actions .btn {
    flex: 1 1 0;
    min-height: 44px;
  }
  .drop-confirm .custom-select,
  .drop-confirm .form-control {
    font-size: 16px;
  }
}
</style>
